import { forwardRef, type ComponentType, type InputHTMLAttributes, type ReactNode } from 'react';
import { WarningCircle, type IconProps } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Field, fieldControlClass, type FieldSize } from '../Field/Field';
import { Icon } from '../Icon';

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  label: ReactNode;
  labelHidden?: boolean;
  help?: ReactNode;
  error?: ReactNode;
  /** Leading icon inside the field (e.g. a magnifying glass on a search field). */
  icon?: ComponentType<IconProps>;
  /** `md` (default) is 48px; `sm` is 40px for desktop toolbars and filter rows. */
  size?: FieldSize;
  fieldClassName?: string;
};

/**
 * A text field on the surface colour with a 3:1 outline, 48px tall, 16px text (so iOS never zooms).
 * Always has a visible label; the placeholder is only an example. Errors say how to fix them.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, labelHidden, help, error, icon, id, className, fieldClassName, size = 'md', ...rest },
  ref,
) {
  return (
    <Field label={label} labelHidden={labelHidden} help={help} error={error} id={id} className={fieldClassName}>
      {({ id: fieldId, describedBy, invalid }) => (
        <div className="relative">
          {icon ? (
            <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-jc-ink-secondary">
              <Icon icon={icon} size="md" />
            </span>
          ) : null}
          <input
            {...rest}
            ref={ref}
            id={fieldId}
            aria-invalid={invalid || undefined}
            aria-describedby={cx(describedBy, rest['aria-describedby']) || undefined}
            className={cx(fieldControlClass(invalid, size), icon && 'pl-11', invalid && 'pr-11', className)}
          />
          {invalid ? (
            <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-jc-ink">
              <Icon icon={WarningCircle} size="md" weight="fill" />
            </span>
          ) : null}
        </div>
      )}
    </Field>
  );
});
